/** Matches backend PublicMenuCacheControl max-age (#614). */
export const MENU_CACHE_TTL_MS = 300_000;
/** v2 keys include the pickup location and expire at salesNextChangeAt. */
const PREFIX = "lumora:menu:v2:";
const INDEX_KEY = PREFIX + "__index";
function storageKey(slug, location = "") {
    const loc = location.trim().toLowerCase();
    const base = PREFIX + encodeURIComponent(slug);
    return loc ? `${base}|${encodeURIComponent(loc)}` : base;
}
function readIndex(store) {
    const raw = store.getItem(INDEX_KEY);
    if (!raw)
        return [];
    try {
        const keys = JSON.parse(raw);
        return Array.isArray(keys) ? keys.filter((key) => typeof key === "string") : [];
    }
    catch {
        return [];
    }
}
function writeIndex(store, keys) {
    try {
        if (keys.length === 0) {
            store.removeItem(INDEX_KEY);
            return;
        }
        store.setItem(INDEX_KEY, JSON.stringify(keys));
    }
    catch {
        // Quota / private mode — the entry may still be readable by its own key.
    }
}
function forget(store, key) {
    writeIndex(store, readIndex(store).filter((saved) => saved !== key));
}
function remember(store, key) {
    const keys = readIndex(store);
    if (!keys.includes(key))
        keys.push(key);
    writeIndex(store, keys);
}
function freshUntil(menu, savedAt, ttlMs) {
    let until = savedAt + ttlMs;
    if (menu.salesNextChangeAt) {
        const next = Date.parse(menu.salesNextChangeAt);
        if (Number.isFinite(next) && next < until)
            until = next;
    }
    return until;
}
/** sessionStorage when available; null in Node / private modes that throw. */
export function defaultMenuCacheStore() {
    try {
        if (typeof globalThis.sessionStorage === "undefined")
            return null;
        globalThis.sessionStorage.getItem(PREFIX + "__probe__");
        return globalThis.sessionStorage;
    }
    catch {
        return null;
    }
}
export function readMenuCache(store, slug, now = Date.now(), ttlMs = MENU_CACHE_TTL_MS, location = "") {
    if (!store || !slug)
        return null;
    const key = storageKey(slug, location);
    const raw = store.getItem(key);
    if (!raw)
        return null;
    try {
        const entry = JSON.parse(raw);
        if (!entry?.menu || typeof entry.savedAt !== "number")
            return null;
        const until = typeof entry.freshUntil === "number" ? entry.freshUntil : entry.savedAt + ttlMs;
        if (now >= until) {
            store.removeItem(key);
            forget(store, key);
            return null;
        }
        return entry.menu;
    }
    catch {
        store.removeItem(key);
        forget(store, key);
        return null;
    }
}
export function writeMenuCache(store, slug, menu, now = Date.now(), location = "", ttlMs = MENU_CACHE_TTL_MS) {
    if (!store || !slug)
        return;
    const key = storageKey(slug, location);
    const entry = { savedAt: now, freshUntil: freshUntil(menu, now, ttlMs), menu };
    try {
        store.setItem(key, JSON.stringify(entry));
        remember(store, key);
    }
    catch {
        // Quota / private mode — ignore.
    }
}
export function clearMenuCache(store, slug) {
    if (!store)
        return;
    const keys = readIndex(store);
    const match = slug ? storageKey(slug) : "";
    const keep = [];
    for (const key of keys) {
        const drop = !slug || key === match || key.startsWith(`${match}|`);
        if (drop)
            store.removeItem(key);
        else
            keep.push(key);
    }
    if (slug)
        store.removeItem(match);
    else {
        const anyStore = store;
        if (typeof anyStore.key === "function" && typeof anyStore.length === "number") {
            const extra = [];
            for (let i = 0; i < anyStore.length; i++) {
                const key = anyStore.key(i);
                if (key?.startsWith(PREFIX) && key !== INDEX_KEY)
                    extra.push(key);
            }
            for (const key of extra)
                store.removeItem(key);
        }
    }
    writeIndex(store, slug ? keep : []);
}
//# sourceMappingURL=menuCache.js.map